import {
  KNOBS,
  PRESETS,
  applyPreset,
  saveConfig,
  type PresetName,
  type TuningConfig,
} from '../physics/config'
import type { DebugFlags } from '../render/debug'
import type { Recorder } from '../input/Replay'

export interface PanelActions {
  onPresetChange(preset: PresetName): void
  onReset(): void
  onCalibrate(): void
  onRecordToggle(): boolean
  onReplayFile(file: File): void
  onToggleCamera(): string
}

/**
 * Desktop tuning panel (DESIGN.md §3). Hand-rolled rather than dat.GUI: it is
 * ~100 lines, has no dependency to keep current, and the same knob list drives
 * the in-VR wrist panel.
 */
export class DomPanel {
  private readonly root: HTMLElement
  private readonly sliders = new Map<keyof TuningConfig, HTMLInputElement>()
  private readonly readouts = new Map<keyof TuningConfig, HTMLElement>()
  private readonly presetButtons = new Map<PresetName, HTMLButtonElement>()
  private recordButton: HTMLButtonElement | null = null
  private offerRow: HTMLElement | null = null

  constructor(
    root: HTMLElement,
    private readonly config: TuningConfig,
    private preset: PresetName,
    private readonly flags: DebugFlags,
    private readonly actions: PanelActions,
  ) {
    this.root = root
    this.build()
  }

  private build(): void {
    this.root.innerHTML = ''
    this.root.append(heading('Tuning'))

    const presets = document.createElement('div')
    presets.className = 'presets'
    for (const name of Object.keys(PRESETS) as PresetName[]) {
      const button = document.createElement('button')
      button.textContent = name
      button.onclick = () => this.selectPreset(name)
      presets.append(button)
      this.presetButtons.set(name, button)
    }
    this.root.append(presets)

    for (const knob of KNOBS) {
      const row = document.createElement('div')
      row.className = 'row'

      const label = document.createElement('label')
      const value = document.createElement('b')
      label.append(document.createTextNode(knob.label), value)
      label.title = knob.hint

      const slider = document.createElement('input')
      slider.type = 'range'
      slider.min = String(knob.min)
      slider.max = String(knob.max)
      slider.step = String(knob.step)
      slider.value = String(this.config[knob.key])
      slider.oninput = () => {
        ;(this.config[knob.key] as number) = Number(slider.value)
        value.textContent = formatValue(Number(slider.value))
        saveConfig(this.config, this.preset)
      }

      value.textContent = formatValue(this.config[knob.key] as number)
      row.append(label, slider)
      this.root.append(row)
      this.sliders.set(knob.key, slider)
      this.readouts.set(knob.key, value)
    }

    this.root.append(section('Debug overlays'))
    const toggles = document.createElement('div')
    toggles.className = 'toggles'
    for (const key of Object.keys(this.flags) as (keyof DebugFlags)[]) {
      const button = document.createElement('button')
      button.textContent = key.replace(/([A-Z])/g, ' $1').toLowerCase()
      const sync = () => button.classList.toggle('active', this.flags[key])
      button.onclick = () => {
        this.flags[key] = !this.flags[key]
        sync()
      }
      sync()
      toggles.append(button)
    }
    this.root.append(toggles)

    this.root.append(section('Session'))
    const actions = document.createElement('div')
    actions.className = 'presets'
    actions.append(
      button('respawn (R)', () => this.actions.onReset()),
      button('recalibrate (C)', () => this.actions.onCalibrate()),
      button('camera (V)', () => this.actions.onToggleCamera()),
    )
    this.root.append(actions)

    const replayRow = document.createElement('div')
    replayRow.className = 'presets'
    this.recordButton = button('● record', () => {
      const recording = this.actions.onRecordToggle()
      this.recordButton!.textContent = recording ? '■ stop + save' : '● record'
      this.recordButton!.classList.toggle('active', recording)
    })
    const load = document.createElement('input')
    load.type = 'file'
    load.accept = 'application/json'
    load.style.display = 'none'
    load.onchange = () => {
      const file = load.files?.[0]
      if (file) this.actions.onReplayFile(file)
      load.value = ''
    }
    replayRow.append(this.recordButton, button('▶ load replay', () => load.click()), load)
    this.root.append(replayRow)

    const hint = document.createElement('div')
    hint.className = 'hint'
    hint.textContent =
      'Pad: triggers = throttles · sticks = per-rocket aim · d-pad/A/Y = grip · LB/RB = lean.\n' +
      'Keys: A/D throttle, W both, arrows aim, [ ] differential, Z/X lean, 1-7 grip, R respawn.'
    hint.style.whiteSpace = 'pre-line'
    this.root.append(hint)

    this.offerRow = document.createElement('div')
    this.offerRow.className = 'presets'
    this.offerRow.style.display = 'none'
    this.root.append(this.offerRow)

    this.syncPresetButtons()
  }

  /**
   * Surface a just-finished headset flight. XR sessions record automatically,
   * and the moment the headset comes off is the only moment the player is
   * looking at this screen — so put the save button in front of them then,
   * rather than hoping they remember it exists.
   */
  offerRecording(recorder: Recorder, standingHeight = 1.7): void {
    if (!this.offerRow) return
    this.offerRow.innerHTML = ''
    const seconds = recorder.durationSeconds.toFixed(0)
    this.offerRow.append(
      button(`⤓ save flight (${seconds}s)`, () => {
        recorder.download(standingHeight, 'headset session')
      }),
    )
    this.offerRow.style.display = ''
  }

  private selectPreset(name: PresetName): void {
    this.preset = name
    applyPreset(this.config, name)
    this.syncFromConfig()
    saveConfig(this.config, name)
    this.actions.onPresetChange(name)
  }

  /** Pull slider positions back from the config, e.g. after a preset change. */
  syncFromConfig(): void {
    for (const knob of KNOBS) {
      const value = this.config[knob.key] as number
      const slider = this.sliders.get(knob.key)
      if (slider) slider.value = String(value)
      const readout = this.readouts.get(knob.key)
      if (readout) readout.textContent = formatValue(value)
    }
    this.syncPresetButtons()
  }

  private syncPresetButtons(): void {
    for (const [name, button] of this.presetButtons) {
      button.classList.toggle('active', name === this.preset)
    }
  }
}

function heading(text: string): HTMLElement {
  const h = document.createElement('h2')
  h.textContent = text
  return h
}

function section(text: string): HTMLElement {
  const div = document.createElement('div')
  div.className = 'sep'
  div.textContent = text
  return div
}

function button(text: string, onClick: () => void): HTMLButtonElement {
  const b = document.createElement('button')
  b.textContent = text
  b.onclick = onClick
  return b
}

function formatValue(value: number): string {
  if (Math.abs(value) >= 10) return value.toFixed(0)
  if (Math.abs(value) >= 1) return value.toFixed(2)
  return value.toFixed(3)
}
